import { ApiClient } from "@hakili/api-client";
import { createQueryClient, type QueryClient } from "@hakili/app-shell";
import type {
  AccountsAnswer,
  CabinetPoolAnswer,
  CabinetTermsAnswer,
  DocumentOnBehalfBody,
  ExpertiseGridAnswer,
  OnboardedCabinetAnswer,
  OrganisationsAnswer,
  StaffReportAnswer,
} from "@hakili/contracts";
import { t } from "@hakili/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type DiagnosticLu, diagnosticFrom } from "./drapeaux.js";
import { parseDealerAccessFacts } from "./dealer-access-facts.js";
import { parseToleranceAnswer } from "./mileage-tolerance.js";
import { partnersFrom } from "./partners.js";
import type { MileageToleranceDoors } from "../routes/tolerance-block.js";
import type { Partner } from "../routes/dealerships.js";
import type { BandCell, DossierRow, FileFeeLineView, TopupLineView } from "../routes/dossiers.js";
import { type SettlementLineView, settlementLinesFrom } from "../routes/settlements.js";
import type { DeveloppementDoors, PipelineView } from "../routes/developpement.js";
import type { FeatureRequestView, FonctionnalitesDoors } from "../routes/fonctionnalites.js";
import type { HoldSignalView, PrioritesDoors, SignalsView } from "../routes/priorites.js";
import type { ClosureReasonView, SupervisionDoors, SupervisionView } from "../routes/supervision.js";
import type { ArrivalView, ArriveesDoors, AssignableView } from "../routes/arrivees.js";
import type { ProposalView, RecommandationsDoors } from "../routes/recommandations.js";
import type { CabinetsDoors } from "../routes/cabinets.js";
import type { TeamDoors, TeamMemberView, TransferLineView } from "../routes/team.js";
import { type BankView, type BanksDoors, bankViewsFrom } from "../routes/partner-banks.js";
import type { AccountsDoors } from "../routes/accounts.js";
import type { ClientAccountView } from "../routes/client-accounts.js";
import type {
  BreachTermsDoors,
  BreachTermsView,
  HoldTermsDoors,
  HoldTermsView,
  PaymentSettingsDoors,
  PaymentSettingsView,
} from "../routes/profil.js";
import type {
  BreachSheetView,
  DealerConventionView,
  JournalEntryView,
  PartnerSheetDoors,
} from "../routes/partner-sheet.js";
import type {
  ArrestView,
  CabinetDayView,
  DayView,
  DemandView,
  ExpertisesDoors,
  EscrowFeeView,
  QueueLine,
} from "../routes/expertises.js";
import type {
  BankConditionsView,
  DossierConditionView,
  DossierDetail,
  DossierDoors,
  FeeView,
  JournalLine,
  PieceView,
  ReportProvenance,
  ReportReading,
  HoldLedgerView,
} from "../routes/dossier.js";
import type { FirstPassReadingView, SynthesisView } from "../routes/first-pass.js";
import type { SubmissionOptionsView } from "../routes/submission-panel.js";
import type { AnalysisChainView, ConsoleUnderwritingView } from "../routes/underwriting-panel.js";
import type { EmploymentVerificationState } from "../routes/employment-verification-panel.js";
import {
  CHALLENGE,
  challenged,
  type Identity,
  identityFrom,
  messageOf,
  reasonOf,
  refused,
  renamed,
  type Session,
  signedIn,
  signedOut,
  START,
} from "@hakili/app-shell";

/**
 * `P0-UIK-021`: the thin part. Every decision lives in `session.ts`; this holds
 * the client, the state and the effects, and does no reasoning of its own.
 *
 * **The client is built once and kept in a ref**, so the access token it holds
 * survives a re-render and dies with the page. `ApiClient` already carries the
 * whole refresh dance , `credentials: "include"`, one in-flight refresh, a
 * retry, and `onSessionEnded` when the refresh itself fails , so nothing here
 * re-implements it. What this adds is where the failure LANDS: at the login
 * screen, with a sentence, never as an exception through the tree.
 */

export interface Console {
  readonly session: Session;
  /**
   * The query client, built here because it is session wiring and not screen
   * wiring: its `onSessionExpired` fires when a `401` outlives the single
   * refresh, and the only correct answer to that is this hook's `signedOut`. A
   * client built beside the mount would need a reference back to this state,
   * and the indirection is the bug: the callback captures the FIRST render's
   * setter and a later sign-out never lands.
   */
  readonly queryClient: QueryClient;
  readonly connecter: (credentials: { email: string; password: string; totpCode?: string }) => Promise<void>;
  readonly deconnecter: () => void;
  readonly enregistrer: (fullName: string) => Promise<void>;
  /**
   * `round CN-A`, `CN-A.0`: **« Une idée ? », live.**
   *
   * `WF-CON-24`/`WF-CON-43`'s own door. `P0-UIK-015` built the context module
   * and `shell/demande.ts` has sat unused since: the rail had no control and
   * the session had no door, so the one field the workflow names as its
   * acceptance criterion , *the context always names the screen the user was
   * actually on* , had nothing to travel through.
   *
   * The context is a VALUE captured at the click and handed here, never read
   * when the modal renders: by then the modal is what is current.
   */
  /**
   * `round CN-A`, `CN-A.1`: the Dossiers screen's three doors. The scope is the
   * door's (`P1-DOS-017`); the band is the responsable's and the administration's
   * and refuses a chargé; mark-seen clears the caller's own flags and no others.
   */
  readonly listDossiers: () => Promise<readonly DossierRow[]>;
  readonly readSupervision: () => Promise<readonly BandCell[]>;
  readonly markDossiersSeen: () => Promise<readonly string[]>;
  /** `round CN-C`, `CN-C.6`: payments' file-fees door. */
  readonly readFileFees: () => Promise<readonly FileFeeLineView[]>;
  /** `T-1c`: Hakili's confirmation of a file fee on evidence (WF-CON-45). */
  readonly collectFileFee: (feeId: string, evidence: File) => Promise<void>;
  /** `T-1d`: the dealers' declared top-ups (row 24), and Hakili's confirmation on evidence. */
  readonly readTopups: () => Promise<readonly TopupLineView[]>;
  readonly confirmTopup: (topupId: string, evidence: File) => Promise<void>;
  /** `T-2`: the two-leg board (row 26), Hakili's leg observed on evidence (row 24). */
  readonly readSettlements: () => Promise<readonly SettlementLineView[]>;
  readonly observeHakiliLeg: (
    settlementId: string,
    evidence: File,
    transferReference: string | null,
  ) => Promise<void>;
  /** `CN-C.1`: « Priorités »' doors; `CN-C.2`: « Développement »'s; `CN-C.3`: « Supervision »'s; `CN-C.4`: « Fonctionnalités »'. */
  readonly prioritesDoors: PrioritesDoors;
  readonly developpementDoors: DeveloppementDoors;
  readonly supervisionDoors: SupervisionDoors;
  readonly fonctionnalitesDoors: FonctionnalitesDoors;
  /** `CN-A.1`: the file's doors, every one of them the api's own. */
  readonly dossierDoors: DossierDoors;
  /** `CN-A.2`: « Arrivées »' doors, the api's own. */
  readonly arriveesDoors: ArriveesDoors;
  /** `WF-CON-11-NEW-BANK-PIPELINE`, `M1`: « Recommandations »' doors, the api's own. */
  readonly recommandationsDoors: RecommandationsDoors;
  /** `CN-A.3`: « Expertises »' doors, and the cabinets'. */
  readonly expertisesDoors: ExpertisesDoors;
  readonly cabinetsDoors: CabinetsDoors;
  /** `round CN-B`, `CN-B.1`: « Équipe »'s doors. */
  readonly teamDoors: TeamDoors;
  /** `round CN-B`, `CN-B.2`: the Banques tab's two doors, and the fiche's three. */
  readonly banksDoors: BanksDoors;
  readonly partnerSheetDoors: PartnerSheetDoors;
  /** `round CN-B`, `CN-B.3`: « Comptes »'s doors and the administration's payment settings. */
  readonly accountsDoors: AccountsDoors;
  readonly paymentSettingsDoors: PaymentSettingsDoors;
  readonly holdTermsDoors: HoldTermsDoors;
  /** `K-2` (resumed): the sanctions' terms, beside the reservation's. */
  readonly breachTermsDoors: BreachTermsDoors;
  readonly mileageToleranceDoors: MileageToleranceDoors;
  readonly submitFeatureRequest: (demande: {
    readonly faire: string;
    readonly manque: string;
    readonly permettrait: string;
    readonly contexte: string;
  }) => Promise<void>;
  /**
   * `P0-IDN-044`. Reads the last code the API minted for a number, or `null`.
   *
   * **Every refusal becomes `null` and none becomes an error**, because the API
   * answers the same `404` to all four , flag off, wrong environment, wrong
   * right, unknown number. A screen that distinguished them would be inventing a
   * difference the API deliberately does not make.
   */
  readonly lireLeCode: (phone: string) => Promise<{ channel: string; code: string } | null>;
  /** `P0-IDN-046`: the last invitation token minted for an address, or `null`. */
  readonly lireLeLien: (email: string) => Promise<string | null>;
  /**
   * `P0-CNW-001`: opens a dealership, and answers what went wrong when it will
   * not open.
   *
   * **This one does NOT collapse every refusal into `null`**, and the contrast
   * with `lireLeLien` above is deliberate. Those reads collapse because the API
   * deliberately answers one `404` to four different causes, so distinguishing
   * them in a screen would invent a difference. Here the API draws real
   * distinctions a person must act on , the name is taken, the offline checks
   * are not recorded, the right is missing , and flattening them would leave a
   * founder staring at a form that says only "non".
   */
  readonly ouvrirConcession: (saisie: {
    nom: string;
    identiteVerifiee: boolean;
    conventionRecue: boolean;
  }) => Promise<{ ok: true; id: string } | { ok: false; message: string }>;
  /**
   * `P0-CNW-002`: names a dealership's administrator and sends the invitation.
   *
   * **`envoye` is reported separately from `ok`**, because `P0-MSG-006` commits
   * the invitation and then dispatches: the account can exist while the mail
   * did not leave, and that is the only case « Renvoyer » is for. Collapsing
   * the two would either hide a failed send or offer a resend for an invitation
   * that already arrived.
   */
  readonly nommerAdministrateur: (
    organisationId: string,
    saisie: { fullName: string; email: string },
  ) => Promise<
    { ok: true; invitationId: string; envoye?: boolean; mailStatus?: string } | { ok: false; message: string }
  >;
  /** `P0-CNW-002`: carries an existing invitation again, creating nothing. */
  /** `P0-PTA-001`: the partner account behind a fiche, from partnerships' door. */
  /** `R64`: the covered cities, so the fiche chooses instead of accepting typing. */
  readonly readCities: () => Promise<readonly { id: string; name: string }[]>;
  /** `R64`: the places inside one city. */
  readonly readCityZones: (cityId: string) => Promise<readonly { code: string; label: string }[]>;
  readonly readPartnerAccount: (
    organisationId: string,
  ) => Promise<
    | { ok: true; adresse: { ville: string | null; quartier: string | null; telephone: string | null } }
    | { ok: false; message: string }
  >;
  /** `P0-PTA-001`: sets it. The api's own refusal is what the screen renders. */
  readonly writePartnerAddress: (
    organisationId: string,
    saisie: { ville: string; quartier: string; telephone: string },
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  readonly renvoyerInvitation: (
    invitationId: string,
  ) => Promise<{ ok: true; envoye: boolean } | { ok: false; message: string }>;
  /**
   * `FX-3`, `M3` (`S2-4`): the burn, `POST /invitations/{id}/revoke`. `null` is the success and a string is
   * the door's own sentence , the shape a screen can render without inventing one.
   */
  readonly revokeInvitationLink: (invitationId: string) => Promise<string | null>;
  /**
   * `P0-CNW-029`: the partner directory, or `null` when the door refused.
   *
   * Through `ApiClient` rather than `fetch`, because `GET /api/v1/organisations`
   * IS declared in the OpenAPI document , so the client that refuses undeclared
   * paths accepts it and the refresh dance applies like anywhere else.
   *
   * **`null` and never a throw**, and the reason is the screen's: a directory
   * that could not be read is not an empty directory, and `Dealerships` renders
   * a different thing for each. Collapsing them would print « Aucune concession »
   * over a network failure, which is the most reassuring possible lie.
   */
  readonly listPartners: () => Promise<readonly Partner[] | null>;

  /**
   * `P0-PLT-038`. Reads the four columns behind every flag's derived answer.
   * `null` when the API refuses, which for this route means the right is
   * missing , the card says so rather than rendering an empty table.
   */
  readonly lireLesDrapeaux: () => Promise<DiagnosticLu | null>;
}

/**
 * `P0-UIK-024`: `amorcer` is `false` on a PUBLIC route.
 *
 * The boot read below exchanges the refresh cookie for a session. On a route
 * whose authorisation is its own token that is the wrong question to ask, and
 * asking it is how dealer-web revived the founder's console session on an
 * invitation screen for a dealership they had never joined. The console mounts
 * the same screen, so it takes the same rule , written here rather than at the
 * call site, because a hook that boots unconditionally cannot be told not to.
 */
/**
 * Round `C6` (`OD-33`): **the cabinet's paper FIRST, by the documents' own
 * doors** , a ticket, the browser's PUT, the confirm , then the transcription
 * naming it. The paper's source is the channel it arrived by; the door refuses
 * a transcription without it, in words.
 */
async function fileReportWithPaper(
  api: ApiClient,
  demandId: string,
  entry: {
    readonly observed: readonly string[];
    readonly estimatedValueAmount: number;
    readonly observations: string;
    readonly receptionChannel: string;
    readonly paper: File;
  },
): Promise<void> {
  const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
    "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
    "post",
    {
      params: { ownerType: "expertiseReport", ownerId: demandId },
      body: {
        name: entry.paper.name,
        mimeType: entry.paper.type,
        sizeBytes: entry.paper.size,
        source: entry.receptionChannel,
      },
    },
  );
  const put = await globalThis.fetch(ticket.url, {
    method: "PUT",
    headers: ticket.headers,
    body: entry.paper,
  });
  if (!put.ok) throw new Error(t("console.dossier.rapportPapierRequis"));
  const confirmed = await api.request<{ documentId: string }>(
    "/api/v1/documents/{ownerType}/{ownerId}/confirm",
    "post",
    { params: { ownerType: "expertiseReport", ownerId: demandId }, body: { ticketId: ticket.id } },
  );
  await api.request<StaffReportAnswer>("/api/v1/expertise/requests/{id}/report", "post", {
    params: { id: demandId },
    body: {
      observed: entry.observed.map((code) => ({ code })),
      estimatedValueAmount: entry.estimatedValueAmount,
      observations: entry.observations,
      entryMode: "equipeHakili",
      receptionChannel: entry.receptionChannel,
      documentId: confirmed.documentId,
    },
  });
}

/**
 * `E-1`, `M1` (`R40`'s third leg): **the door's answer, parsed in one exported place.**
 *
 * It is exported so the feed can be PROVEN. Inline in the callback nothing could assert that a
 * field the door answers survives the parse , which is where a value dies quietly: the door right,
 * the screen right, every spec green, and the value never arriving. `FX-1b` exported `dossierFrom`
 * for exactly this reason after `closure.at` nearly went the same way.
 *
 * `envoye` keeps its meaning , the mail LEFT , and `mailStatus` carries the row's own word beside
 * it, including the `aEnvoyer` a boolean could never say. Absent means the API did not say, and the
 * console invents neither.
 */
export function invitationAnswerFrom(body: Record<string, unknown>): {
  ok: true;
  invitationId: string;
  envoye?: boolean;
  mailStatus?: string;
} {
  return {
    ok: true,
    invitationId: body["invitationId"] as string,
    ...(typeof body["delivered"] === "boolean" ? { envoye: body["delivered"] } : {}),
    ...(typeof body["mailStatus"] === "string" ? { mailStatus: body["mailStatus"] } : {}),
  };
}

export function useSession(
  origin: string,
  fetchImpl?: typeof globalThis.fetch,
  options?: { readonly amorcer?: boolean },
): Console {
  const [session, setSession] = useState<Session>(START);

  const client = useRef<ApiClient | null>(null);
  client.current ??= new ApiClient({
    origin,
    // `P0-IDN-063`: which surface this application is, so the api names its
    // session's cookie for it and no other application can revive it.
    surface: "console",
    // The refresh itself failed, so the session is genuinely over. This is
    // the ONLY place a silent refresh may end, and it ends at the login
    // screen rather than in the error boundary.
    onSessionEnded: () => {
      setSession(signedOut());
    },
    ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
  });
  const api = client.current;

  const queries = useRef<QueryClient | null>(null);
  queries.current ??= createQueryClient({
    onSessionExpired: () => {
      api.setAccessToken(null);
      setSession(signedOut());
    },
  });
  const queryClient = queries.current;

  /** Reads `/me` and either seats the person or refuses with the server's own words. */
  const identify = useCallback(async (): Promise<void> => {
    try {
      const me = await api.request<unknown>("/api/v1/me", "get");
      const identity = identityFrom(me);
      setSession(identity === null ? refused(t("console.session.inattendue")) : signedIn(identity));
    } catch (failure) {
      // The TOTP wall answers every route but the enrolment pair. It is not a
      // bad password and must not be shown as one.
      const reason = reasonOf(failure);
      setSession(
        reason === CHALLENGE
          ? challenged(messageOf(failure, t("common.error.inattendue")))
          : signedOut(messageOf(failure, t("common.error.reseau"))),
      );
    }
  }, [api]);

  /**
   * **On boot, try the cookie.** The access token was deliberately forgotten
   * when the page unloaded; the `SameSite=Strict` refresh cookie was not, and
   * it is same-site with this host by `A41`'s design. So a reload asks the
   * server who this is, and a person who was signed in stays signed in.
   */
  const amorcer = options?.amorcer ?? true;
  useEffect(() => {
    if (!amorcer) return undefined;
    const run = { live: true };
    void (async () => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/refresh", "post");
        if (!run.live) return;
        if (typeof tokens.accessToken === "string") {
          api.setAccessToken(tokens.accessToken);
          await identify();
          return;
        }
        setSession(signedOut());
      } catch {
        // No cookie, or a spent one. Anonymous is the answer, not an error.
        if (run.live) setSession(signedOut());
      }
    })();
    return () => {
      run.live = false;
    };
  }, [api, identify, amorcer]);

  const connecter = useCallback(
    async (credentials: { email: string; password: string; totpCode?: string }): Promise<void> => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/staff/login", "post", {
          body: credentials,
        });
        if (typeof tokens.accessToken !== "string") {
          setSession(refused(t("console.session.inattendue")));
          return;
        }
        api.setAccessToken(tokens.accessToken);
        await identify();
      } catch (failure) {
        setSession(refused(messageOf(failure, t("common.error.reseau"))));
        // Re-thrown on purpose. `Connexion` was built to render the server's
        // OWN refusal , "Identifiants incorrects", "Trop de tentatives", "Code
        // incorrect. Il vous reste N essai(s)" , and to reveal the code field
        // when the server asks for a code. Swallowing the error here would leave that screen
        // with nothing to show and would be forking it in all but name.
        throw failure;
      }
    },
    [api, identify],
  );

  /**
   * `P0-IDN-044`, and **the one call in this console that does not go through
   * `ApiClient`**, deliberately.
   *
   * `ApiClient.request` refuses a path the OpenAPI document does not declare ,
   * *"is not an operation this API declares"* , and this route is absent from
   * that document ON PURPOSE: it is not a product route of 04, 4.2, and the
   * ruling that granted it requires its absence. Declaring it to satisfy the
   * client would publish the endpoint in the very artefact that must not name
   * it. So this reaches for `fetch` and says why, rather than widening the
   * product's own surface to accommodate a demonstration device.
   *
   * The bearer is read off the client, so the token still lives in exactly one
   * place and still dies with the page.
   */
  /**
   * Through `ApiClient` and not through `fetch`, which is the difference
   * between this route and `P0-IDN-044`'s: the diagnostic is DECLARED in the
   * OpenAPI document, so the client that refuses undeclared paths accepts it,
   * and the refresh dance applies like anywhere else.
   */
  const lireLesDrapeaux = useCallback(async (): Promise<DiagnosticLu | null> => {
    try {
      return diagnosticFrom(await api.request<unknown>("/api/v1/admin/flags/diagnostics", "get"));
    } catch {
      // A refusal here is a missing right, and the card says exactly that. It
      // is deliberately not distinguished from a transport failure: neither is
      // a reading, and inventing a second sentence for a state this screen
      // cannot tell apart would be the blindness it exists to end.
      return null;
    }
  }, [api]);

  /**
   * `P0-CNW-029`. Parsed by `partnersFrom`, which reads the SERVER's contract:
   * a row's `administrator` is an object or `null`, and its `state` is one of
   * the api's own words.
   */
  const listPartners = useCallback(async (): Promise<readonly Partner[] | null> => {
    try {
      return partnersFrom(await api.request<OrganisationsAnswer>("/api/v1/organisations", "get"));
    } catch {
      return null;
    }
  }, [api]);

  /**
   * `P0-IDN-046`. The same shape as `lireLeCode` beside it, and for the same
   * reason: the API makes every refusal one answer, so this makes every failure
   * one `null` rather than inventing a difference the route does not draw.
   */
  const lireLeLien = useCallback(
    async (email: string): Promise<string | null> => {
      const token = api.accessToken;
      if (token === null) return null;
      try {
        const response = await globalThis.fetch(
          `${origin}/api/v1/test/invitation/${encodeURIComponent(email)}`,
          { headers: { accept: "application/json", authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return null;
        const body = (await response.json()) as { token?: unknown };
        return typeof body.token === "string" ? body.token : null;
      } catch {
        return null;
      }
    },
    [api, origin],
  );

  const ouvrirConcession = useCallback(
    async (saisie: {
      nom: string;
      identiteVerifiee: boolean;
      conventionRecue: boolean;
    }): Promise<{ ok: true; id: string } | { ok: false; message: string }> => {
      const token = api.accessToken;
      if (token === null) return { ok: false, message: t("console.concession.erreurSession") };
      try {
        const response = await globalThis.fetch(`${origin}/api/v1/organisations`, {
          method: "POST",
          headers: {
            accept: "application/json",
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: saisie.nom,
            // The API's vocabulary, not the screen's. `concessionnaire` is what
            // `OrganisationType` calls a dealership, and the French label the
            // founder reads lives in the catalogue.
            type: "concessionnaire",
            identityVerified: saisie.identiteVerifiee,
            conventionReceived: saisie.conventionRecue,
          }),
        });
        const body = (await response.json().catch(() => ({}))) as {
          id?: unknown;
          error?: { message?: unknown };
        };
        if (response.ok && typeof body.id === "string") return { ok: true, id: body.id };
        // **The API's own sentence, shown as written.** It is already in French
        // and already precise , "Les vérifications hors ligne doivent être
        // enregistrées avant l'ouverture." tells a founder exactly what to do.
        // Replacing it with a generic line here would lose that.
        const message =
          typeof body.error?.message === "string" && body.error.message !== ""
            ? body.error.message
            : t("console.concession.erreurInattendue");
        return { ok: false, message };
      } catch {
        return { ok: false, message: t("console.concession.erreurReseau") };
      }
    },
    [api, origin],
  );

  const nommerAdministrateur = useCallback(
    async (
      organisationId: string,
      saisie: { fullName: string; email: string },
    ): Promise<
      | { ok: true; invitationId: string; envoye?: boolean; mailStatus?: string }
      | { ok: false; message: string }
    > => {
      const token = api.accessToken;
      if (token === null) return { ok: false, message: t("console.concession.erreurSession") };
      try {
        const response = await globalThis.fetch(
          `${origin}/api/v1/organisations/${encodeURIComponent(organisationId)}/first-admin`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "content-type": "application/json",
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(saisie),
          },
        );
        const body = (await response.json().catch(() => ({}))) as {
          invitationId?: unknown;
          delivered?: unknown;
          mailStatus?: unknown;
          error?: { message?: unknown };
        };
        if (response.ok && typeof body.invitationId === "string") {
          return invitationAnswerFrom(body);
        }
        const message =
          typeof body.error?.message === "string" && body.error.message !== ""
            ? body.error.message
            : t("console.concession.erreurInattendue");
        return { ok: false, message };
      } catch {
        return { ok: false, message: t("console.concession.erreurReseau") };
      }
    },
    [api, origin],
  );

  /**
   * `P0-PTA-001`: the partner account behind a fiche , where this dealership is.
   *
   * Read through `partnerships`' own door, never from a column on the
   * organisation: 05 §1950 gives a partner's address ONE source, and a second
   * copy is exactly what that sentence forbids.
   */
  /**
   * `P0-PTA-002` (`R64`, rule one): **the covered cities, from the door.**
   *
   * A typed city is what let « Segou » and a Bamako quartier disagree and
   * re-routed a dealership four hundred kilometres. The fiche chooses.
   */
  const readCities = useCallback(async (): Promise<readonly { id: string; name: string }[]> => {
    try {
      const response = await globalThis.fetch(`${origin}/api/v1/cities`, {
        headers: { accept: "application/json" },
      });
      if (!response.ok) return [];
      const carried = (await response.json()) as { data?: unknown };
      return Array.isArray(carried.data) ? (carried.data as readonly { id: string; name: string }[]) : [];
    } catch {
      return [];
    }
  }, [origin]);

  /** The places inside ONE city , Bamako's quartiers, or the single zone elsewhere. */
  const readCityZones = useCallback(
    async (cityId: string): Promise<readonly { code: string; label: string }[]> => {
      const token = api.accessToken;
      if (token === null) return [];
      try {
        const response = await globalThis.fetch(
          `${origin}/api/v1/cities/${encodeURIComponent(cityId)}/zones`,
          { headers: { accept: "application/json", authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return [];
        const carried = (await response.json()) as { data?: unknown };
        return Array.isArray(carried.data)
          ? (carried.data as readonly { code: string; label: string }[])
          : [];
      } catch {
        return [];
      }
    },
    [api, origin],
  );

  const readPartnerAccount = useCallback(
    async (
      organisationId: string,
    ): Promise<
      | { ok: true; adresse: { ville: string | null; quartier: string | null; telephone: string | null } }
      | { ok: false; message: string }
    > => {
      const token = api.accessToken;
      if (token === null) return { ok: false, message: t("console.concession.erreurSession") };
      try {
        const response = await globalThis.fetch(
          `${origin}/api/v1/partners/${encodeURIComponent(organisationId)}/account`,
          { headers: { accept: "application/json", authorization: `Bearer ${token}` } },
        );
        const carried = (await response.json().catch(() => ({}))) as Record<string, unknown> & {
          error?: { message?: unknown };
        };
        if (response.ok) {
          const lu = (key: string): string | null => {
            const value = carried[key];
            // `null` and not `""`: the fiche must tell "no address" from "an
            // address set to nothing", and it renders the two differently.
            return typeof value === "string" && value !== "" ? value : null;
          };
          return {
            ok: true,
            adresse: { ville: lu("city"), quartier: lu("district"), telephone: lu("phone") },
          };
        }
        const message =
          typeof carried.error?.message === "string" && carried.error.message !== ""
            ? carried.error.message
            : t("console.concession.erreurInattendue");
        return { ok: false, message };
      } catch {
        return { ok: false, message: t("console.concession.erreurReseau") };
      }
    },
    [api, origin],
  );

  /**
   * `P0-PTA-001`: the console sets a dealership's address.
   *
   * The api refuses an uncovered city and an unrecognised Bamako quartier **in
   * its own words**, and those words are what the fiche renders. The screen
   * composes no refusal: the covered cities changed this very round, and a
   * second implementation of that list would already be wrong.
   */
  const writePartnerAddress = useCallback(
    async (
      organisationId: string,
      saisie: { ville: string; quartier: string; telephone: string },
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      const token = api.accessToken;
      if (token === null) return { ok: false, message: t("console.concession.erreurSession") };
      try {
        const response = await globalThis.fetch(
          `${origin}/api/v1/partners/${encodeURIComponent(organisationId)}/account/address`,
          {
            method: "PUT",
            headers: {
              accept: "application/json",
              "content-type": "application/json",
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              city: saisie.ville,
              district: saisie.quartier,
              phone: saisie.telephone,
            }),
          },
        );
        if (response.ok) return { ok: true };
        const carried = (await response.json().catch(() => ({}))) as { error?: { message?: unknown } };
        const message =
          typeof carried.error?.message === "string" && carried.error.message !== ""
            ? carried.error.message
            : t("console.concession.erreurInattendue");
        return { ok: false, message };
      } catch {
        return { ok: false, message: t("console.concession.erreurReseau") };
      }
    },
    [api, origin],
  );

  const renvoyerInvitation = useCallback(
    async (invitationId: string): Promise<{ ok: true; envoye: boolean } | { ok: false; message: string }> => {
      const token = api.accessToken;
      if (token === null) return { ok: false, message: t("console.concession.erreurSession") };
      try {
        const response = await globalThis.fetch(
          `${origin}/api/v1/invitations/${encodeURIComponent(invitationId)}/resend`,
          { method: "POST", headers: { accept: "application/json", authorization: `Bearer ${token}` } },
        );
        const carried = (await response.json().catch(() => ({}))) as {
          delivered?: unknown;
          error?: { message?: unknown };
        };
        if (response.ok) {
          // `P0-IDN-062`: the door now says whether the mail LEFT. Reporting
          // `ok` alone is exactly the lie this round removed , a resend that
          // revoked the old link and sent nothing would read as a success and
          // the invitee would be left with no working link at all.
          return { ok: true, envoye: carried.delivered !== false };
        }
        const message =
          typeof carried.error?.message === "string" && carried.error.message !== ""
            ? carried.error.message
            : t("console.concession.erreurInattendue");
        return { ok: false, message };
      } catch {
        return { ok: false, message: t("console.concession.erreurReseau") };
      }
    },
    [api, origin],
  );

  /**
   * `FX-3`, `M3` (`S2-4`): **the burn, from the partner's card.** `M1` built
   * `POST /api/v1/invitations/{id}/revoke`; this is the hand that reaches it. The answer is the door's own
   * sentence on a refusal and `null` on success , the shape `renvoyerInvitation` has, so a screen cannot
   * report a success the door did not give.
   */
  const revokeInvitationLink = useCallback(
    async (invitationId: string): Promise<string | null> => {
      try {
        await api.request<unknown>("/api/v1/invitations/{id}/revoke", "post", {
          params: { id: invitationId },
        });
        return null;
      } catch (failure) {
        return messageOf(failure, t("console.concession.erreurInattendue"));
      }
    },
    [api],
  );

  const lireLeCode = useCallback(
    async (phone: string): Promise<{ channel: string; code: string } | null> => {
      const token = api.accessToken;
      if (token === null) return null;
      try {
        const response = await globalThis.fetch(`${origin}/api/v1/test/otp/${encodeURIComponent(phone)}`, {
          headers: { accept: "application/json", authorization: `Bearer ${token}` },
        });
        if (!response.ok) return null;
        const body = (await response.json()) as { channel?: unknown; code?: unknown };
        if (typeof body.code !== "string") return null;
        return { channel: typeof body.channel === "string" ? body.channel : "", code: body.code };
      } catch {
        // A transport failure and a refusal are one answer here, because the API
        // already makes all four refusals one answer. Distinguishing them on the
        // screen would invent a difference the route does not make.
        return null;
      }
    },
    [api, origin],
  );

  const deconnecter = useCallback((): void => {
    // Revoke first, then forget: a logout that only clears the tab leaves the
    // refresh cookie alive, and the next visit signs the person back in.
    void api.request("/api/v1/auth/logout", "post").catch(() => undefined);
    api.setAccessToken(null);
    setSession(signedOut());
  }, [api]);

  const listDossiers = useCallback(async (): Promise<readonly DossierRow[]> => {
    const answer = await api.request<{ dossiers?: readonly DossierRow[] }>("/api/v1/dossiers", "get");
    return answer.dossiers ?? [];
  }, [api]);

  const readSupervision = useCallback(async (): Promise<readonly BandCell[]> => {
    const answer = await api.request<{ cells?: readonly BandCell[] }>("/api/v1/dossiers/supervision", "get");
    return answer.cells ?? [];
  }, [api]);

  /**
   * `T-1c` (WF-CON-45, `P2-PAY-003`): **Hakili confirms a buyer's file fee on evidence** , the
   * proof filed FIRST by the documents' own doors under the fee (`fileFeeEvidence`, a ticket, the
   * browser's PUT, the confirm), then the collect door naming it. Never a free-text receipt.
   */
  const collectFileFee = useCallback(
    async (feeId: string, evidence: File): Promise<void> => {
      const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
        "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
        "post",
        {
          params: { ownerType: "fileFeeEvidence", ownerId: feeId },
          body: { name: evidence.name, mimeType: evidence.type, sizeBytes: evidence.size },
        },
      );
      const put = await globalThis.fetch(ticket.url, {
        method: "PUT",
        headers: ticket.headers,
        body: evidence,
      });
      if (!put.ok) throw new Error(t("console.dossiers.preuveRequise"));
      const confirmed = await api.request<{ documentId: string }>(
        "/api/v1/documents/{ownerType}/{ownerId}/confirm",
        "post",
        { params: { ownerType: "fileFeeEvidence", ownerId: feeId }, body: { ticketId: ticket.id } },
      );
      await api.request<unknown>("/api/v1/payments/file-fees/{id}/collect", "post", {
        params: { id: feeId },
        body: { documentId: confirmed.documentId },
      });
    },
    [api],
  );

  /**
   * `T-1d` (WF-DLR-13 step 3 as amended, `P2-PAY-008`): **Hakili confirms a dealer's declared
   * top-up on evidence** , the proof of receipt filed FIRST by the documents' doors under the
   * top-up (`topupEvidence`), then the confirm door naming it; the credit movement is the door's.
   */
  const confirmTopup = useCallback(
    async (topupId: string, evidence: File): Promise<void> => {
      const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
        "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
        "post",
        {
          params: { ownerType: "topupEvidence", ownerId: topupId },
          body: { name: evidence.name, mimeType: evidence.type, sizeBytes: evidence.size },
        },
      );
      const put = await globalThis.fetch(ticket.url, {
        method: "PUT",
        headers: ticket.headers,
        body: evidence,
      });
      if (!put.ok) throw new Error(t("console.dossiers.rechargePreuveRequise"));
      const confirmed = await api.request<{ documentId: string }>(
        "/api/v1/documents/{ownerType}/{ownerId}/confirm",
        "post",
        { params: { ownerType: "topupEvidence", ownerId: topupId }, body: { ticketId: ticket.id } },
      );
      await api.request<unknown>("/api/v1/payments/topups/{id}/confirm", "post", {
        params: { id: topupId },
        body: { documentId: confirmed.documentId },
      });
    },
    [api],
  );

  /**
   * `T-2`, `M3 (a)` (WF-CON-18 step 5's second row): **Hakili's leg observed on evidence** , the
   * proof filed FIRST by the documents' doors under the settlement (`settlementEvidence`), then
   * the door naming it; `SettlementCompleted` and the bon are the door's when this was the second leg.
   */
  const observeHakiliLeg = useCallback(
    async (settlementId: string, evidence: File, transferReference: string | null): Promise<void> => {
      const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
        "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
        "post",
        {
          params: { ownerType: "settlementEvidence", ownerId: settlementId },
          body: { name: evidence.name, mimeType: evidence.type, sizeBytes: evidence.size },
        },
      );
      const put = await globalThis.fetch(ticket.url, {
        method: "PUT",
        headers: ticket.headers,
        body: evidence,
      });
      if (!put.ok) throw new Error(t("console.reglements.preuveRequise"));
      const confirmed = await api.request<{ documentId: string }>(
        "/api/v1/documents/{ownerType}/{ownerId}/confirm",
        "post",
        { params: { ownerType: "settlementEvidence", ownerId: settlementId }, body: { ticketId: ticket.id } },
      );
      await api.request<unknown>("/api/v1/payments/settlements/{id}/hakili-leg", "post", {
        params: { id: settlementId },
        body: {
          documentId: confirmed.documentId,
          ...(transferReference === null ? {} : { transferReference }),
        },
      });
    },
    [api],
  );

  const readSettlements = useCallback(async (): Promise<readonly SettlementLineView[]> => {
    const answer = await api.request<unknown>("/api/v1/payments/settlements", "get");
    return settlementLinesFrom(answer);
  }, [api]);

  const readTopups = useCallback(async (): Promise<readonly TopupLineView[]> => {
    const answer = await api.request<{ topups?: readonly TopupLineView[] }>("/api/v1/payments/topups", "get");
    return answer.topups ?? [];
  }, [api]);

  const readFileFees = useCallback(async (): Promise<readonly FileFeeLineView[]> => {
    const answer = await api.request<{ lines?: readonly FileFeeLineView[] }>(
      "/api/v1/payments/file-fees",
      "get",
    );
    return answer.lines ?? [];
  }, [api]);

  /** `round CN-C`: the four screens' doors, every one of them the api's own. */
  const prioritesDoors = useMemo<PrioritesDoors>(
    () => ({
      readSignals: async () => api.request<SignalsView>("/api/v1/partners/signals", "get"),
      // `K-1`, `M3 (d)`: the reservation signals, `dossier`'s door; the chase, the file's own.
      readHoldSignals: async () => {
        const answer = await api.request<{ signals?: readonly HoldSignalView[] }>(
          "/api/v1/dossiers/signals",
          "get",
        );
        return answer.signals ?? [];
      },
      chaseClient: async (dossierId) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/pieces/relance-all", "post", {
          params: { id: dossierId },
        });
      },
      logExchange: async (organisationId, input) => {
        await api.request<unknown>("/api/v1/partners/{organisationId}/journal", "post", {
          params: { organisationId },
          body: input,
        });
      },
    }),
    [api],
  );
  const developpementDoors = useMemo<DeveloppementDoors>(
    () => ({
      readPipeline: async () => api.request<PipelineView>("/api/v1/partners/pipeline", "get"),
      advance: async (accountId) => {
        await api.request<unknown>("/api/v1/partners/pipeline/{accountId}/advance", "post", {
          params: { accountId },
        });
      },
      logExchange: async (organisationId, input) => {
        await api.request<unknown>("/api/v1/partners/{organisationId}/journal", "post", {
          params: { organisationId },
          body: input,
        });
      },
    }),
    [api],
  );
  const supervisionDoors = useMemo<SupervisionDoors>(
    () => ({
      readSupervisionOfAgent: async () =>
        api.request<SupervisionView>("/api/v1/intelligence/supervision", "get"),
      // `K-1`, `M4`: the closures' coded motives, counted by `dossier`'s door.
      readClosureReasons: async () => {
        const answer = await api.request<{ reasons?: readonly ClosureReasonView[] }>(
          "/api/v1/dossiers/closures/reasons",
          "get",
        );
        return answer.reasons ?? [];
      },
    }),
    [api],
  );
  const fonctionnalitesDoors = useMemo<FonctionnalitesDoors>(
    () => ({
      readFeatureRequests: async () => {
        const answer = await api.request<{ requests?: readonly FeatureRequestView[] }>(
          "/api/v1/feature-requests",
          "get",
        );
        return answer.requests ?? [];
      },
    }),
    [api],
  );

  const markDossiersSeen = useCallback(async (): Promise<readonly string[]> => {
    const answer = await api.request<{ dossierIds?: readonly string[] }>(
      "/api/v1/dossiers/mark-seen",
      "post",
    );
    return answer.dossierIds ?? [];
  }, [api]);

  /**
   * `round CN-A`, `CN-A.1`: **the file's doors**, one object so the screen
   * takes them whole, memoised on the api like everything else here. The
   * demand for a dossier is found by reading the responsable's queue and
   * matching on `dossierId`, as the corridor does , the expertise door has no
   * lookup by file, and inventing one for a screen would be a door nobody
   * recorded.
   */
  const dossierDoors = useMemo<DossierDoors>(
    () => ({
      // `P1-CNW-064` (D-1031-18, #901's door): the dealer-access facts, newest first, parsed by the contract's READ twin; the App hands this door only to a seat that holds `voirEtRelancerDossiers`.
      readDealerAccessFacts: async (id) =>
        parseDealerAccessFacts(
          await api.request<unknown>("/api/v1/dossiers/{id}/dealer-access-facts", "get", { params: { id } }),
        ),
      readDossier: async (id) => {
        const answer = await api.request<{
          dossier: DossierDetail;
          actions: readonly string[];
          fee: FeeView | null;
          verdict: string | null;
          report: ReportProvenance | null;
          filterFailure?: string | null;
          filterComputation?: { eligibleCount: number } | null;
          conditions?: readonly DossierConditionView[] | null;
          bankConditions?: BankConditionsView | null;
          stagesBypassed?: readonly string[];
          settlement?: SettlementLineView | null;
        }>("/api/v1/dossiers/{id}", "get", { params: { id } });
        return answer;
      },
      // `U-3`, `M1`: the synthesis, derived by intelligence at every read (WF-CON-04 step 4).
      readSynthesis: async (id) => {
        const answer = await api.request<{ synthesis: SynthesisView | null }>(
          "/api/v1/dossiers/{id}/synthesis",
          "get",
          {
            params: { id },
          },
        );
        return answer.synthesis;
      },
      // `U-1`, `M3`: « Lecture de Hakili » by its door, and the chargée's two acts by hers.
      readFirstPass: async (id) => {
        const answer = await api.request<{ reading: FirstPassReadingView | null }>(
          "/api/v1/dossiers/{id}/first-pass",
          "get",
          {
            params: { id },
          },
        );
        return answer.reading;
      },
      // `AA-3c`: the chain behind the one button « Lancer l'analyse Hakili », where it stands and the press that starts it.
      readAnalysis: async (id) => {
        const answer = await api.request<{ analysis: AnalysisChainView | null }>(
          "/api/v1/dossiers/{id}/analysis",
          "get",
          { params: { id } },
        );
        return answer.analysis;
      },
      // `AA-4b` (`D-1018-AA2`): the banks to choose from at Prêt, and the press that sends the file to the ones she ticked.
      readSubmissionOptions: (id) =>
        api.request<SubmissionOptionsView>("/api/v1/dossiers/{id}/submission-options", "get", {
          params: { id },
        }),
      submitToBanks: async (id, bankIds) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/submissions", "post", {
          params: { id },
          body: { banques: [...bankIds] },
        });
      },
      launchAnalysis: async (id) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/analysis/launch", "post", {
          params: { id },
          body: {},
        });
      },
      // Round 11a (`OD-37`, D-1010-3): « Analyse de Hakili », the console run by its door and the chargée's own launch.
      readUnderwriting: async (id) => {
        const answer = await api.request<{ underwriting: ConsoleUnderwritingView | null }>(
          "/api/v1/dossiers/{id}/underwriting",
          "get",
          { params: { id } },
        );
        return answer.underwriting;
      },
      // Round 10a (`OD-37`, workflow §1.5, WF-CON-13): the employment verification, the chargée's own act, and the send gate.
      readEmploymentVerification: (id) =>
        api.request<EmploymentVerificationState>("/api/v1/dossiers/{id}/employment-verification", "get", {
          params: { id },
        }),
      recordEmploymentVerification: async (id, body) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/employment-verification", "post", {
          params: { id },
          body: { ...body },
        });
      },
      actOnFirstPassField: async (id, key, act, value) => {
        await api.request<unknown>(
          act === "confirmee"
            ? "/api/v1/dossiers/{id}/first-pass/fields/{field}/confirm"
            : act === "corrigee"
              ? "/api/v1/dossiers/{id}/first-pass/fields/{field}/correct"
              : "/api/v1/dossiers/{id}/first-pass/fields/{field}/manual",
          "post",
          { params: { id, field: key }, body: act === "confirmee" ? {} : { value } },
        );
      },
      // `T-1b`, `M2` (WF-CON-18): the chargée's tick on a condition préalable, and its undoing.
      liftCondition: async (id, cle, how) => {
        await api.request<unknown>(
          how === "lift"
            ? "/api/v1/dossiers/{id}/conditions/{cle}/lift"
            : "/api/v1/dossiers/{id}/conditions/{cle}/reopen",
          "post",
          { params: { id, cle } },
        );
      },
      // Round `C6`, `M4`: the report of record read; the chargé's request for a correction.
      readReport: async (id) => {
        const answer = await api.request<{ report: ReportReading | null }>(
          "/api/v1/dossiers/{id}/rapport",
          "get",
          {
            params: { id },
          },
        );
        return answer.report;
      },
      regenerateSheet: async (dossierId) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/rapport/lecture-hakili", "post", {
          params: { id: dossierId },
        });
      },
      requestCorrection: async (demandId, reason) => {
        await api.request<unknown>("/api/v1/expertise/requests/{id}/correction-request", "post", {
          params: { id: demandId },
          body: { reason },
        });
      },
      readJournal: async (id) => {
        const answer = await api.request<{ journal?: readonly JournalLine[] }>(
          "/api/v1/dossiers/{id}/journal",
          "get",
          {
            params: { id },
          },
        );
        return answer.journal ?? [];
      },
      // Round `L-2`, `M3` (B10): the bank's offer recorded by hand, the bank's figures as typed.
      recordOffer: async (submissionId, offer) => {
        await api.request<unknown>("/api/v1/submissions/{id}/offer", "post", {
          params: { id: submissionId },
          body: { ...offer },
        });
      },
      readSubmissions: async (id) => {
        const answer = await api.request<{
          submissions?: readonly {
            id: string;
            bankName: string;
            status: string;
            submittedAt: string;
            decidedAt: string | null;
            completion?: {
              requestedAt: string;
              clientMustAct: boolean;
              items: readonly { label: string; documentCode: string | null }[];
            } | null;
            refusal?: { code: string; label: string } | null;
            offerEnd?: { reason: "not-retained" | "client-declined" | "expired"; at: string } | null;
            withdrawnAt?: string | null;
          }[];
          refusalReasons?: readonly { code: string; label: string }[];
        }>("/api/v1/dossiers/{id}/submissions", "get", { params: { id } });
        // `L-3b`, M0 (d): the rows and the closed list ride the same read.
        return { submissions: answer.submissions ?? [], refusalReasons: answer.refusalReasons ?? [] };
      },
      // Round `L-3b`, `M2` (M0 (d)): the bank's refusal recorded by hand, with its motive (WF-CON-14).
      recordRefusal: async (submissionId, reasonCode) => {
        await api.request<unknown>("/api/v1/submissions/{id}/refusal", "post", {
          params: { id: submissionId },
          body: { reasonCode },
        });
      },
      // `FX-3`, `M2 (d)` (`D6`): « Signaler au contrôle » , the chargé pings the seats that may confirm a
      // received transfer. It confirms nothing; row 28's holders confirm from the séquestre.
      flagVerificationTransfer: async (id) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/fees/verification/flag-control", "post", {
          params: { id },
        });
      },
      // Round `L-3b`, `M2` (`P1-LND-015`): « Toutes les banques ont refusé », the chargée's declaration.
      allRefused: async (id) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/all-refused", "post", { params: { id } });
      },
      // Round `L-3`, `M2`: the chargé resubmits after a completion (`P1-LND-014`).
      resubmit: async (submissionId) => {
        await api.request<unknown>("/api/v1/submissions/{id}/resubmit", "post", {
          params: { id: submissionId },
        });
      },
      // Round `L-3`, `M2`: the relance from the bank's return , the same door, WF-CON-04's line.
      chaseForBank: async (id, piece) => {
        const answer = await api.request<{ link?: string | null }>(
          "/api/v1/dossiers/{id}/pieces/{piece}/relance",
          "post",
          { params: { id, piece }, body: { context: "bank-completion" } },
        );
        return { link: answer.link ?? null };
      },
      readPieces: async (id) => {
        const answer = await api.request<{ pieces?: readonly PieceView[]; manquants?: number }>(
          "/api/v1/dossiers/{id}/pieces",
          "get",
          { params: { id } },
        );
        return { pieces: answer.pieces ?? [], manquants: answer.manquants ?? 0 };
      },
      readDemandFor: async (dossierId) => {
        const answer = await api.request<{ requests?: readonly { id: string; dossierId: string | null }[] }>(
          "/api/v1/expertise/requests",
          "get",
        );
        return (answer.requests ?? []).find((one) => one.dossierId === dossierId)?.id ?? null;
      },
      readGrid: async () => {
        const answer = await api.request<ExpertiseGridAnswer>("/api/v1/expertise/grid", "get");
        return answer.families;
      },
      readAssignable: async () => {
        const answer = await api.request<{ charges?: readonly { id: string; fullName: string }[] }>(
          "/api/v1/dossiers/assignable",
          "get",
        );
        return answer.charges ?? [];
      },
      advanceDossier: async (id, action, reason) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/advance", "post", {
          params: { id },
          body: reason === undefined ? { action } : { action, reason },
        });
      },
      returnToCollecte: async (id, reason, ask) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/remettre-en-collecte", "post", {
          params: { id },
          body: {
            reason,
            // `AA-3d2`: what the chargée asks the client to complete , both optional; without them the call is
            // today's, exactly.
            ...(ask === undefined || ask.items.length === 0
              ? {}
              : {
                  items: ask.items.map((one) => ({
                    text: one.text,
                    ...(one.pieceCode === null ? {} : { pieceCode: one.pieceCode }),
                  })),
                }),
            ...(ask?.note === undefined || ask.note === null ? {} : { note: ask.note }),
          },
        });
      },
      closeDossier: async (id, motif, code) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/close", "post", {
          params: { id },
          body: { motif, code },
        });
      },
      // `K-1`, `M3 (d)`: the ledger of days, derived by the server on each read.
      readHolds: async (id) => {
        const answer = await api.request<{ ledger: HoldLedgerView | null }>(
          "/api/v1/dossiers/{id}/holds",
          "get",
          {
            params: { id },
          },
        );
        return answer.ledger;
      },
      reassignDossier: async (id, userId, reason) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/assignment", "post", {
          params: { id },
          body: { userId, reason },
        });
      },
      /**
       * `round S4-14`: **the SAME three-crossing door the buyer's own upload already is** , a
       * ticket, the browser's own `PUT`, the confirmation , never a second upload mechanism. A
       * `multi_file` pièce admits several files picked at once, uploaded one after another through
       * this same door, exactly as `collectFileFee`/`confirmTopup` already upload a single file.
       *
       * `round N+1`: no `auteur` parameter , the server resolves the staff member's own name from
       * the authenticated session; the client never carries a value that would be written as such.
       */
      fileOnBehalf: async (id, piece, canal, note, files, declaredMonths) => {
        for (const [index, file] of files.entries()) {
          const ticket = await api.request<{
            ticket: { id: string; url: string; headers: Record<string, string> };
          }>("/api/v1/dossiers/{id}/pieces/{piece}/on-behalf/upload-ticket", "post", {
            params: { id, piece },
            body: { canal, fileName: file.name, mimeType: file.type, sizeBytes: file.size },
          });
          const put = await globalThis.fetch(ticket.ticket.url, {
            method: "PUT",
            headers: ticket.ticket.headers,
            body: file,
          });
          if (!put.ok) throw new Error(t("console.dossier.televerserEchec"));
          await api.request<unknown>("/api/v1/dossiers/{id}/pieces/{piece}/on-behalf", "post", {
            params: { id, piece },
            // `#834`: the body is the contract's own type (`documentOnBehalfBodySchema`, strict): an empty note is OMITTED, never `null`, and nothing else is sent.
            body: {
              canal,
              ...(note === "" ? {} : { note }),
              ticketId: ticket.ticket.id,
              // `CONSOLE-MONTHS-QUESTION`: the months THIS file holds, as the chargée declared them (a multi-file pièce only; the door reads 1 where absent).
              ...(declaredMonths?.[index] === undefined ? {} : { declaredMonths: declaredMonths[index] }),
            } satisfies DocumentOnBehalfBody,
          });
        }
      },
      // `C7.3`: the chase mints ONE link for ONE pièce, answered once as a path
      // on the client app , a credential the screen holds and never re-reads.
      chaseDocument: async (id, piece) => {
        const answer = await api.request<{ link?: string | null }>(
          "/api/v1/dossiers/{id}/pieces/{piece}/relance",
          "post",
          {
            params: { id, piece },
          },
        );
        return { link: answer.link ?? null };
      },
      // `S4-6`: the mirror , the same shape, the vendor named instead of the client.
      chaseSellerDocument: async (id, piece) => {
        const answer = await api.request<{ link?: string | null }>(
          "/api/v1/dossiers/{id}/pieces/{piece}/relance-vendeur",
          "post",
          {
            params: { id, piece },
          },
        );
        return { link: answer.link ?? null };
      },
      chaseAll: async (id) => {
        const answer = await api.request<{
          pieces?: readonly { piece: { code: string }; link: string | null }[];
        }>("/api/v1/dossiers/{id}/pieces/relance-all", "post", { params: { id } });
        return (answer.pieces ?? []).map((one) => ({ documentCode: one.piece.code, link: one.link }));
      },
      // `C7.1`: a received pièce rejected with its reason, in the chargé's own name.
      rejectDocument: async (id, piece, reason) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/pieces/{piece}/reject", "post", {
          params: { id, piece },
          body: {
            reason,
            auteur: session.status === "authenticated" ? session.identity.fullName : undefined,
          },
        });
      },
      // `VISITE-SANS-OBJET`: the visite technique marked « sans objet » with a reason, and the undo , in the chargé's own name.
      markNotApplicable: async (id, piece, reason) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/pieces/{piece}/sans-objet", "post", {
          params: { id, piece },
          body: { reason },
        });
      },
      undoNotApplicable: async (id, piece, reason) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/pieces/{piece}/sans-objet/undo", "post", {
          params: { id, piece },
          body: { reason },
        });
      },
      // `C7.4`: the civility and the holder's name, read off the identity document.
      verifyIdentity: async (id, gender, holderName) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/pieces/pieceIdentite/verification", "post", {
          params: { id },
          body: {
            gender,
            holderName,
            auteur: session.status === "authenticated" ? session.identity.fullName : undefined,
          },
        });
      },
      // `S4-9`: the pièce opened before judgement , a presigned GET, answered once;
      // `null` when the pièce carries no file at all (filed on behalf, no scan).
      consultDocument: async (id, piece) => {
        const answer = await api.request<{ url?: string | null }>(
          "/api/v1/dossiers/{id}/pieces/{piece}/consult",
          "post",
          {
            params: { id, piece },
          },
        );
        return { url: answer.url ?? null };
      },
      fileReport: (demandId, entry) => fileReportWithPaper(api, demandId, entry),
    }),
    [api],
  );

  /**
   * `round CN-A`, `CN-A.2`: **« Arrivées »' doors.** The flow by
   * `GET /dossiers/arrivals`, the selector by `GET /dossiers/assignable` , the
   * same door the file's reassignment reads, now answering who is absent and
   * until when (`WF-CON-22` step 3) , and the correction by the one write
   * path every reassignment takes (`WF-CON-23`).
   */
  const arriveesDoors = useMemo<ArriveesDoors>(
    () => ({
      readArrivals: async () => {
        const answer = await api.request<{ arrivals?: readonly ArrivalView[] }>(
          "/api/v1/dossiers/arrivals",
          "get",
        );
        return answer.arrivals ?? [];
      },
      readAssignableWithAvailability: async () => {
        const answer = await api.request<{ charges?: readonly AssignableView[] }>(
          "/api/v1/dossiers/assignable",
          "get",
        );
        return answer.charges ?? [];
      },
      reassignDossier: async (id, userId, reason) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/assignment", "post", {
          params: { id },
          body: { userId, reason },
        });
      },
    }),
    [api],
  );

  /**
   * `WF-CON-11-NEW-BANK-PIPELINE`, `M1` (`OD-42`): **« Recommandations »' doors.** The chargé's open proposals
   * by `GET /proposed-sends`, and her two acts by the approval and the dismissal , each the api's own, the
   * refusal a sentence the server wrote and the screen shows as it came.
   */
  const recommandationsDoors = useMemo<RecommandationsDoors>(
    () => ({
      readProposals: async () => {
        const answer = await api.request<{ proposals?: readonly ProposalView[] }>(
          "/api/v1/proposed-sends",
          "get",
        );
        return answer.proposals ?? [];
      },
      approve: async (id) => {
        await api.request<unknown>("/api/v1/proposed-sends/{id}/approval", "post", { params: { id } });
      },
      dismiss: async (id) => {
        await api.request<unknown>("/api/v1/proposed-sends/{id}/dismissal", "post", { params: { id } });
      },
    }),
    [api],
  );

  /**
   * `round CN-A`, `CN-A.3`: **« Expertises »' doors.** The queue with the
   * proposal, the days with their arrest, one day's composition with its sheet
   * and signal, the schedule (with the exception stated when the day is
   * arrested), the sheet's send (the link comes back once and is kept in the
   * screen's own state, never here), the grid and the report, and the
   * séquestre , `P1-PAY-010`'s list and `C3-A`'s confirmation.
   */
  const expertisesDoors = useMemo<ExpertisesDoors>(
    () => ({
      readQueue: async () =>
        await api.request<{ queue: readonly QueueLine[]; arrest: ArrestView }>(
          "/api/v1/expertise/queue",
          "get",
        ),
      readDays: async () => {
        const answer = await api.request<{ days?: readonly DayView[] }>("/api/v1/expertise/days", "get");
        return answer.days ?? [];
      },
      readDay: async (dayId) => {
        const answer = await api.request<{ cabinets?: readonly CabinetDayView[] }>(
          "/api/v1/expertise/days/{id}",
          "get",
          {
            params: { id: dayId },
          },
        );
        return answer.cabinets ?? [];
      },
      readDemands: async () => {
        const answer = await api.request<{ requests?: readonly DemandView[] }>(
          "/api/v1/expertise/requests",
          "get",
        );
        return answer.requests ?? [];
      },
      readGrid: async () => {
        const answer = await api.request<ExpertiseGridAnswer>("/api/v1/expertise/grid", "get");
        return answer.families;
      },
      readFees: async () => {
        const answer = await api.request<{ fees?: readonly EscrowFeeView[] }>(
          "/api/v1/payments/verification-fees",
          "get",
        );
        return answer.fees ?? [];
      },
      schedule: async (demandId, cabinetId, expertiseDayId, afterArrestOverride) => {
        await api.request<unknown>("/api/v1/expertise/requests/{id}/schedule", "post", {
          params: { id: demandId },
          body: { cabinetId, expertiseDayId, afterArrestOverride },
        });
      },
      sendSheet: async (dayId, cabinetId) => {
        const answer = await api.request<{ link: string }>(
          "/api/v1/expertise/days/{id}/cabinets/{cabinetId}/tour-sheet",
          "post",
          { params: { id: dayId, cabinetId }, body: { channel: "whatsapp" } },
        );
        return { link: answer.link };
      },
      fileReport: (demandId, entry) => fileReportWithPaper(api, demandId, entry),
      reassignStop: async (dayId, fromCabinetId, sellerOrganisationId, toCabinetId) => {
        await api.request<unknown>(
          "/api/v1/expertise/days/{id}/cabinets/{cabinetId}/stops/{sellerOrganisationId}/reassign",
          "post",
          { params: { id: dayId, cabinetId: fromCabinetId, sellerOrganisationId }, body: { toCabinetId } },
        );
      },
      confirmTransfer: async (feeId, amount, bankReference, receivedOn) => {
        await api.request<unknown>("/api/v1/payments/verification-fees/{id}/confirm-transfer", "post", {
          params: { id: feeId },
          body: { amount, bankReference, receivedOn },
        });
      },
    }),
    [api],
  );

  /**
   * `round CN-B`, `CN-B.1`: **« Équipe »'s seven doors.** The workload and
   * the transfer are `dossier`'s (`/team`); availability, deactivation and the
   * invitation are `identity`'s (`/users`); the unit reassignment is the same
   * single write path « Arrivées » and the file use, with its ORIGIN said.
   */
  const teamDoors = useMemo<TeamDoors>(
    () => ({
      readWorkload: async () => {
        const answer = await api.request<{ members?: readonly TeamMemberView[] }>(
          "/api/v1/team/workload",
          "get",
        );
        return answer.members ?? [];
      },
      setAvailability: async (userId, availability, returnsOn) => {
        await api.request<unknown>("/api/v1/users/{id}/availability", "patch", {
          params: { id: userId },
          body: availability === "absent" ? { availability, returnsOn } : { availability },
        });
      },
      // `U-3c`, `M0 (a)`: the panel names the RUN it displayed, so the decision points at the
      // proposal the responsable actually read and the door audits no second one.
      reassign: async (dossierId, userId, reason, shownRunId) => {
        await api.request<unknown>("/api/v1/dossiers/{id}/assignment", "post", {
          params: { id: dossierId },
          body: { userId, reason, origin: "team", ...(shownRunId === null ? {} : { shownRunId }) },
        });
      },
      transferPreview: async (userId) => {
        const answer = await api.request<{ lines?: readonly TransferLineView[] }>(
          "/api/v1/team/{userId}/portfolio/transfer",
          "get",
          { params: { userId } },
        );
        return answer.lines ?? [];
      },
      transfer: async (userId) => {
        await api.request<unknown>("/api/v1/team/{userId}/portfolio/transfer", "post", {
          params: { userId },
        });
      },
      deactivate: async (userId) => {
        await api.request<unknown>("/api/v1/users/{id}/deactivate", "post", {
          params: { id: userId },
          body: {},
        });
      },
      invite: async (input) => {
        // The door CARRIES the invitation; its answer names the id and the
        // expiry, never the link (6.4), and nothing here reads it back.
        await api.request<unknown>("/api/v1/users", "post", { body: input });
      },
      // `K-1`, `M2 (d)`: the count from the departure's own preview, the move by its door.
      readPartnerPortfolio: async (userId) => {
        const answer = await api.request<{ partnersHeld?: number | null }>(
          "/api/v1/users/{id}/deactivation",
          "get",
          { params: { id: userId } },
        );
        return answer.partnersHeld ?? null;
      },
      transferPartnerPortfolio: async (userId, toUserId) => {
        const answer = await api.request<{ moved?: number }>(
          "/api/v1/users/{id}/portfolio/transfer",
          "post",
          {
            params: { id: userId },
            body: toUserId === null ? {} : { toUserId },
          },
        );
        return answer.moved ?? 0;
      },
    }),
    [api],
  );

  /**
   * `round CN-B`, `CN-B.2`: **the Banques tab's doors, on the cabinet's
   * pattern** , the organisation by its door, then what lending's door takes,
   * then the first responsible invited by identity's own door, which carries
   * the invitation and never hands the link back.
   */
  const banksDoors = useMemo<BanksDoors>(
    () => ({
      readBanks: async () => {
        // `PARTNERS-DONE-STATE`, `M2b`: the list is PARSED at the boundary with the contracts package's mirror of the
        // OpenAPI item (`bankListAnswerSchema`), never cast: `active` is the panel's membership and a missing or
        // mistyped one is a refusal here, not an « Au panel » invented on the screen.
        return bankViewsFrom(await api.request<unknown>("/api/v1/banks", "get"));
      },
      openBank: async (input) => {
        const opened = await api.request<{ id: string }>("/api/v1/organisations", "post", {
          body: {
            name: input.name,
            type: "banque",
            identityVerified: input.identityVerified,
            conventionReceived: input.conventionReceived,
          },
        });
        await api.request<unknown>("/api/v1/banks", "post", { body: { organisationId: opened.id } });
        await api.request<unknown>("/api/v1/organisations/{id}/first-admin", "post", {
          params: { id: opened.id },
          body: { fullName: input.responsibleName, email: input.responsibleEmail },
        });
      },
    }),
    [api],
  );

  /** `CN-B.2`, WF-CON-36: the fiche's journal, and lending's bank row for its health. */
  const partnerSheetDoors = useMemo<PartnerSheetDoors>(
    () => ({
      readJournal: async (organisationId) => {
        const answer = await api.request<{ entries?: readonly JournalEntryView[]; humanExchanges?: number }>(
          "/api/v1/partners/{organisationId}/journal",
          "get",
          { params: { organisationId } },
        );
        return { entries: answer.entries ?? [], humanExchanges: answer.humanExchanges ?? 0 };
      },
      logExchange: async (organisationId, type, body) => {
        await api.request<unknown>("/api/v1/partners/{organisationId}/journal", "post", {
          params: { organisationId },
          body: { type, body },
        });
      },
      // `T-1d`: the dealer's prepaid balance, the ledger's sum the account door carries by port.
      // `K-2` (resumed), `M4`: the fiche's « Manquements » and the two acts on a claim, by the doors.
      readBreaches: async (organisationId) =>
        api.request<BreachSheetView>("/api/v1/partners/{organisationId}/breaches", "get", {
          params: { organisationId },
        }),
      settleClaim: async (organisationId, claimId) => {
        await api.request<unknown>("/api/v1/partners/{organisationId}/breach-claims/{id}/settle", "post", {
          params: { organisationId, id: claimId },
        });
      },
      waiveClaim: async (organisationId, claimId, note) => {
        await api.request<unknown>("/api/v1/partners/{organisationId}/breach-claims/{id}/waive", "post", {
          params: { organisationId, id: claimId },
          body: { note },
        });
      },
      readDealerHealth: async (organisationId) => {
        const answer = await api.request<{
          prepaidBalance?: unknown;
          prepaidLow?: unknown;
          prepaidLowThreshold?: unknown;
          onlineVehicles?: unknown;
        }>("/api/v1/partners/{organisationId}/account", "get", { params: { organisationId } });
        // `T-2c`, `M2 (5)`: signal 7 beside the sum, both the door's. `FX-3`, `M4 (b)`: and « Véhicules en
        // ligne » beside them , the SAME request, because it is the same door's answer and a second call for
        // one number is a second chance to disagree with the first.
        return {
          prepaid:
            typeof answer.prepaidBalance === "number"
              ? {
                  balance: answer.prepaidBalance,
                  low: answer.prepaidLow === true,
                  threshold:
                    typeof answer.prepaidLowThreshold === "number" ? answer.prepaidLowThreshold : null,
                }
              : null,
          onlineVehicles: typeof answer.onlineVehicles === "number" ? answer.onlineVehicles : null,
        };
      },
      readBank: async (organisationId) => {
        const answer = await api.request<{ banks?: readonly BankView[] }>("/api/v1/banks", "get");
        const listed = (answer.banks ?? []).find((one) => one.organisationId === organisationId) ?? null;
        if (listed === null) return null;
        // `L-1`, `M2`: the bank's own record carries the convention; the list does not.
        return api.request<BankView>("/api/v1/banks/{id}", "get", { params: { id: listed.id } });
      },
      // `OD-43`, `M1`: the four criteria the Hakili filter reads, row 14's door.
      recordCriteria: async (bankId, input) => {
        await api.request<unknown>("/api/v1/banks/{id}/criteria", "put", {
          params: { id: bankId },
          body: input,
        });
      },
      // `PRET-BANKS`: admit a bank to the panel the filter walks, or take it off , the existing door, `ouvrirOrganisation`.
      recordPanel: async (bankId, active) => {
        await api.request<unknown>("/api/v1/banks/{id}/panel", "put", {
          params: { id: bankId },
          body: { active },
        });
      },
      recordConvention: async (bankId, input) => {
        await api.request<unknown>("/api/v1/banks/{id}/convention", "put", {
          params: { id: bankId },
          body: input,
        });
      },
      // `T-1c`, `M3`: the dealer's convention term against the standard, and its pen.
      readDealerConvention: (organisationId) =>
        api.request<DealerConventionView>("/api/v1/partners/{organisationId}/convention", "get", {
          params: { organisationId },
        }),
      recordDealerConvention: async (organisationId, input) => {
        await api.request<unknown>("/api/v1/partners/{organisationId}/convention", "put", {
          params: { organisationId },
          body: input,
        });
      },
      // `CN-C.5`, WF-CON-37 step 2: lending's own door, the one state.
      adjustPrefilter: async (bankId, input) => {
        await api.request<unknown>("/api/v1/banks/{id}/prefilter", "put", {
          params: { id: bankId },
          body: { criterion: "quotite", thresholdPct: input.thresholdPct, label: input.label },
        });
      },
    }),
    [api],
  );

  /** `CN-B.3`, WF-CON-40: the consolidated accounts, and the drawing's « Ouvrir une organisation ». */
  const accountsDoors = useMemo<AccountsDoors>(
    () => ({
      readAccounts: async () => {
        const answer = await api.request<AccountsAnswer>("/api/v1/accounts", "get");
        return answer.data;
      },
      openOrganisation: async (input) => {
        // The door refuses an organisation whose offline checks are not
        // recorded (02 §2.8.2); the drawing's modal draws no checkbox, and the
        // administration opening one from here attests both , the same two
        // facts the cabinet's and the bank's forms take as checkboxes.
        const opened = await api.request<{ id: string }>("/api/v1/organisations", "post", {
          body: { name: input.name, type: input.type, identityVerified: true, conventionReceived: true },
        });
        if (input.type === "banque") {
          await api.request<unknown>("/api/v1/banks", "post", { body: { organisationId: opened.id } });
        }
        await api.request<unknown>("/api/v1/organisations/{id}/first-admin", "post", {
          params: { id: opened.id },
          body: { fullName: input.responsibleName, email: input.responsibleEmail },
        });
      },
      // Round 8, `M2` (WF-CON-40 extended): the client accounts' three doors.
      findClients: async (query) => {
        const answer = await api.request<{ data?: readonly ClientAccountView[] }>("/api/v1/clients", "get", {
          query: { q: query },
        });
        return answer.data ?? [];
      },
      // Partner accounts, `M1`: the administration's override on any partner account. The reason travels to the audit trail.
      deactivateAccount: async (userId, reason) => {
        await api.request<unknown>("/api/v1/accounts/{userId}/deactivate", "post", {
          params: { userId },
          body: { reason },
        });
      },
      // Partner accounts, `M2`: the irreversible one. The name typed back travels with the reason and the door checks both.
      erasePartnerUser: async (userId, input) => {
        await api.request<unknown>("/api/v1/accounts/{userId}/erase", "post", {
          params: { userId },
          body: input,
        });
      },
      deactivateClient: async (clientId, reason) => {
        await api.request<unknown>("/api/v1/clients/{id}/deactivate", "post", {
          params: { id: clientId },
          body: { reason },
        });
      },
      reactivateClient: async (clientId, reason) => {
        await api.request<unknown>("/api/v1/clients/{id}/reactivate", "post", {
          params: { id: clientId },
          body: { reason },
        });
      },
      // Round 9, `M3`: the erasure. The name typed back travels with the reason and the door checks it.
      eraseClient: async (clientId, input) => {
        await api.request<unknown>("/api/v1/clients/{id}/erase", "post", {
          params: { id: clientId },
          body: input,
        });
      },
    }),
    [api],
  );

  /** `CN-B.3`, WF-CON-43: the payment settings, `parametrerPaiements`'s two doors. */
  const paymentSettingsDoors = useMemo<PaymentSettingsDoors>(
    () => ({
      readSettings: async () => {
        const answer = await api.request<{ settings: PaymentSettingsView }>(
          "/api/v1/payments/settings",
          "get",
        );
        return answer.settings;
      },
      publishSettings: async (input) => {
        const answer = await api.request<{ settings: PaymentSettingsView }>(
          "/api/v1/payments/settings",
          "post",
          {
            body: input,
          },
        );
        return answer.settings;
      },
    }),
    [api],
  );

  // `K-1`, `M3 (b)`: the reservation's two terms, their own door beside the fees'.
  /** `K-2` (resumed), `M4`: the sanctions' terms, `partnerships`' door under row 27's family. */
  const breachTermsDoors = useMemo<BreachTermsDoors>(
    () => ({
      readBreachTerms: async () => {
        const answer = await api.request<{ settings: BreachTermsView }>(
          "/api/v1/partners/breach-settings",
          "get",
        );
        return answer.settings;
      },
      publishBreachTerms: async (input) => {
        const answer = await api.request<{ settings: BreachTermsView }>(
          "/api/v1/partners/breach-settings",
          "post",
          {
            body: input,
          },
        );
        return answer.settings;
      },
    }),
    [api],
  );

  /** `P1-CNW-063` (D-1101-2): Hakili's mileage tolerance, `parametrerPaiements`'s two doors; the live answer is parsed by the contract's READ twin (A48). */
  const mileageToleranceDoors = useMemo<MileageToleranceDoors>(
    () => ({
      readTolerance: async () => {
        const answer = await api.request<unknown>("/api/v1/expertise/mileage-tolerance", "get");
        return parseToleranceAnswer(answer);
      },
      publishTolerance: async (input) => {
        const answer = await api.request<unknown>("/api/v1/expertise/mileage-tolerance", "post", {
          body: input,
        });
        return parseToleranceAnswer(answer);
      },
    }),
    [api],
  );

  const holdTermsDoors = useMemo<HoldTermsDoors>(
    () => ({
      readHoldTerms: async () => {
        const answer = await api.request<{ settings: HoldTermsView }>(
          "/api/v1/dossiers/hold-settings",
          "get",
        );
        return answer.settings;
      },
      publishHoldTerms: async (input) => {
        const answer = await api.request<{ settings: HoldTermsView }>(
          "/api/v1/dossiers/hold-settings",
          "post",
          {
            body: input,
          },
        );
        return answer.settings;
      },
    }),
    [api],
  );

  /** The cabinets' two doors, in the walk's order: the organisation, then the cabinet on it. */
  const cabinetsDoors = useMemo<CabinetsDoors>(
    () => ({
      readCabinets: async () => {
        const answer = await api.request<CabinetPoolAnswer>("/api/v1/expertise/cabinets", "get");
        return answer.cabinets;
      },
      openCabinet: async (input) => {
        const opened = await api.request<{ id: string }>("/api/v1/organisations", "post", {
          body: {
            name: input.name,
            type: "cabinet",
            identityVerified: input.identityVerified,
            conventionReceived: input.contractReceived,
          },
        });
        await api.request<OnboardedCabinetAnswer>("/api/v1/expertise/cabinets", "post", {
          body: { ...input, organisationId: opened.id },
        });
      },
      // `round C5-2`, `M1`: the responsable's invitation , identity's
      // first-admin door on the cabinet's organisation, the link for expert-web.
      // `round C5-2`, `M4`: the fiche's terms, by the onboarding's own right.
      amendTerms: async (input) => {
        await api.request<CabinetTermsAnswer>("/api/v1/expertise/cabinets/{id}/terms", "put", {
          params: { id: input.cabinetId },
          body: { feeAmount: input.feeAmount, minimumCapacity: input.minimumCapacity },
        });
      },
      inviteResponsible: async (input) => {
        await api.request<unknown>("/api/v1/organisations/{id}/first-admin", "post", {
          params: { id: input.organisationId },
          body: { fullName: input.fullName, email: input.email, phone: input.phone },
        });
      },
    }),
    [api],
  );

  const submitFeatureRequest = useCallback(
    async (demande: {
      readonly faire: string;
      readonly manque: string;
      readonly permettrait: string;
      readonly contexte: string;
    }): Promise<void> => {
      // The door's own field names, and `app` is the SERVER's to infer from the
      // surface header , an app that reported its own name from a body field
      // would eventually report someone else's.
      await api.request<unknown>("/api/v1/feature-requests", "post", {
        body: {
          goal: demande.faire,
          missing: demande.manque,
          benefit: demande.permettrait,
          screenLabel: demande.contexte,
        },
      });
    },
    [api],
  );

  const enregistrer = useCallback(
    async (fullName: string): Promise<void> => {
      await api.request("/api/v1/me", "patch", { body: { fullName } });
      setSession((current) => renamed(current, fullName));
    },
    [api],
  );

  return useMemo(
    () => ({
      session,
      queryClient,
      connecter,
      deconnecter,
      enregistrer,
      submitFeatureRequest,
      listDossiers,
      readSupervision,
      markDossiersSeen,
      readFileFees,
      collectFileFee,
      readTopups,
      confirmTopup,
      readSettlements,
      observeHakiliLeg,
      prioritesDoors,
      developpementDoors,
      supervisionDoors,
      fonctionnalitesDoors,
      dossierDoors,
      arriveesDoors,
      recommandationsDoors,
      expertisesDoors,
      cabinetsDoors,
      teamDoors,
      banksDoors,
      partnerSheetDoors,
      accountsDoors,
      paymentSettingsDoors,
      holdTermsDoors,
      breachTermsDoors,
      mileageToleranceDoors,
      lireLeCode,
      listPartners,
      ouvrirConcession,
      nommerAdministrateur,
      renvoyerInvitation,
      revokeInvitationLink,
      readPartnerAccount,
      writePartnerAddress,
      readCities,
      readCityZones,
      lireLeLien,
      lireLesDrapeaux,
    }),
    [
      session,
      queryClient,
      connecter,
      deconnecter,
      enregistrer,
      submitFeatureRequest,
      listDossiers,
      readSupervision,
      markDossiersSeen,
      readFileFees,
      collectFileFee,
      readTopups,
      confirmTopup,
      readSettlements,
      observeHakiliLeg,
      prioritesDoors,
      developpementDoors,
      supervisionDoors,
      fonctionnalitesDoors,
      dossierDoors,
      arriveesDoors,
      recommandationsDoors,
      expertisesDoors,
      cabinetsDoors,
      lireLeCode,
      listPartners,
      lireLeLien,
      lireLesDrapeaux,
    ],
  );
}

export type { Identity };
